import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/services/auth';
import {
  SiteOption,
  sitesQueryOptions,
  useSiteByIdQuery,
  useSitesByIdsQuery,
} from '@/services/sites';

type SitesComboboxProps = {
  id?: string;
  container?: HTMLElement | null;
  placeholder?: string;
  className?: string;
} & (
  | {
      multiple: true;
      value?: string[];
      onChange?: (siteIds: string[]) => void;
    }
  | {
      multiple?: false;
      value?: string;
      onChange?: (siteId: string | undefined) => void;
    }
);

export const SitesCombobox = (props: SitesComboboxProps) => {
  if (props.multiple) {
    return <MultipleSitesCombobox {...props} />;
  }
  return <SingleSitesCombobox {...props} />;
};

const SingleSitesCombobox = ({
  id,
  value,
  onChange,
  placeholder,
  className,
  container,
}: {
  id?: string | undefined;
  container?: HTMLElement | null;
  placeholder?: string | undefined;
  className?: string | undefined;
  value?: string | undefined;
  onChange?: ((siteId: string | undefined) => void) | undefined;
}) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const [siteSearch, setSiteSearch] = useState('');
  const debouncedSearch = useDebounce(siteSearch);

  const { data: sites } = useQuery({
    ...sitesQueryOptions(token, {
      search: debouncedSearch,
    }),
    enabled: !!token && !value,
  });
  const { data: initialSite } = useSiteByIdQuery(value);

  const selectedSite = value
    ? (sites?.find((s) => s.id === value) ?? initialSite ?? null)
    : null;

  useEffect(() => {
    setSiteSearch(selectedSite?.name ?? '');
  }, [selectedSite]);

  const handleSiteChange = (site: SiteOption | null) => {
    onChange?.(site?.id);
  };

  return (
    <Combobox
      items={sites}
      value={selectedSite}
      onValueChange={handleSiteChange}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder ?? t('site.selectSite')}
        className={cn('w-82', className)}
        value={siteSearch}
        onChange={(e) => {
          setSiteSearch(e.target.value);
          handleSiteChange(null);
        }}
        showClear
      />
      <ComboboxContent container={container}>
        <ComboboxEmpty>{t('site.noSitesFound')}</ComboboxEmpty>
        <ComboboxList>
          {(site: SiteOption) => (
            <ComboboxItem key={site.id} value={site}>
              {site.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

const MultipleSitesCombobox = ({
  id,
  value,
  onChange,
  placeholder,
  className,
}: {
  id?: string | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
  value?: string[];
  onChange?: ((siteIds: string[]) => void) | undefined;
}) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const anchor = useComboboxAnchor();
  const [siteSearch, setSiteSearch] = useState('');
  const debouncedSearch = useDebounce(siteSearch);

  const selectedIds = useMemo(() => value ?? [], [value]);

  const { data: sites } = useQuery(
    sitesQueryOptions(token, { search: debouncedSearch }),
  );
  const { data: selectedSites } = useSitesByIdsQuery(selectedIds);

  const items = useMemo(() => {
    const mergedSites = [...(selectedSites ?? []), ...(sites ?? [])];
    return uniqBy(mergedSites, (site) => site.id);
  }, [sites, selectedSites]);

  const selectedValue = useMemo(
    () => items.filter((site) => selectedIds.includes(site.id)),
    [items, selectedIds],
  );

  const handleChange = (nextSites: SiteOption[]) => {
    onChange?.(nextSites.map((site) => site.id));
  };

  return (
    <Combobox
      multiple
      autoHighlight
      items={items}
      value={selectedValue}
      onValueChange={handleChange}
      inputValue={siteSearch}
      onInputValueChange={(next) => setSiteSearch(next)}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
    >
      <ComboboxChips ref={anchor} className={cn('w-82', className)}>
        <ComboboxValue>
          {(chosen: SiteOption[]) => (
            <>
              {chosen.map((site) => (
                <ComboboxChip key={site.id} aria-label={site.name}>
                  {site.name}
                </ComboboxChip>
              ))}
              <ComboboxChipsInput
                id={id}
                placeholder={
                  chosen.length > 0 ? '' : (placeholder ?? t('site.selectSite'))
                }
              />
            </>
          )}
        </ComboboxValue>
      </ComboboxChips>
      <ComboboxContent anchor={anchor}>
        <ComboboxEmpty>{t('site.noSitesFound')}</ComboboxEmpty>
        <ComboboxList>
          {(site: SiteOption) => (
            <ComboboxItem key={site.id} value={site}>
              {site.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
